import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, AlertCircle, ArrowRight, Shield } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { TurnstileBox } from '@/components/TurnstileBox';
import { toast } from 'sonner';

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetup, setMfaSetup] = useState<any>(null);
  const [setupComplete, setSetupComplete] = useState<any>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiFetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          password, 
          totp: usingBackupCode ? undefined : totp,
          backup_code: usingBackupCode ? backupCode : undefined,
          turnstile_token: turnstileToken
        }),
      });

      const data = await response.json();

      if (response.status === 403 && data.requiresCaptcha) {
        setShowTurnstile(true);
        setIsLoading(false);
        toast.info('Security verification required');
        return;
      }

      if (data.mfa_required) {
        setMfaRequired(true);
        setError('');
        return;
      }

      if (data.mfa_setup) {
        setMfaSetup(data);
        setError('');
        return;
      }

      if (data.mfa_setup_complete) {
        setMfaSetup(null);
        setSetupComplete(data);
        setError('');
        return;
      }

      if (data.success) {
        navigate('/admin/panel');
      } else {
        setError(data.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded-2xl p-10 shadow-xl space-y-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto border border-emerald-500/20">
                <Shield className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-display font-bold text-zinc-950">MFA Setup Complete</h2>
              <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest leading-relaxed">
                Emergency Recovery Codes Generated
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 p-6 rounded-2xl space-y-4">
              <p className="text-[10px] text-amber-900 font-bold uppercase tracking-widest flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Store these safely
              </p>
              <div className="grid grid-cols-2 gap-2">
                {setupComplete.backup_codes.map((code: string, idx: number) => (
                  <code key={idx} className="bg-white/80 p-2 rounded border border-amber-200 text-center font-mono text-xs font-bold text-zinc-900">
                    {code}
                  </code>
                ))}
              </div>
              <p className="text-[9px] text-amber-800 italic font-medium leading-relaxed">
                These codes are one-time use and allow you to access your account if you lose your authenticator. Store them offline or in a secure password manager.
              </p>
            </div>

            <button
              onClick={() => navigate('/admin/panel')}
              className="w-full bg-zinc-950 text-white font-bold py-4 rounded-xl hover:bg-zinc-900 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
            >
              Enter Admin Panel
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
      <div className="w-full max-w-sm">
        <div className="bg-white border border-zinc-200 rounded-2xl p-10 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-500/5 blur-3xl transition-all pointer-events-none" />
          
          <div className="flex flex-col items-center mb-10 text-center">
            <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-6 border border-cyan-500/20">
              <Shield className="w-8 h-8 text-cyan-500" />
            </div>
            <h1 className="text-2xl font-display font-bold text-zinc-950 tracking-tight mb-1">Admin Login</h1>
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest leading-none">Authorized access only</p>
          </div>
 
          <form onSubmit={handleLogin} className="space-y-6">
            {!mfaRequired && !mfaSetup && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Username</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within/input:text-cyan-500 transition-colors" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3.5 pl-12 pr-6 text-sm text-zinc-900 focus:border-cyan-500/50 transition-all outline-none placeholder:text-zinc-400"
                      placeholder="Enter username"
                      required
                    />
                  </div>
                </div>
     
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within/input:text-cyan-500 transition-colors" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3.5 pl-12 pr-6 text-sm text-zinc-900 focus:border-cyan-500/50 transition-all outline-none placeholder:text-zinc-400"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {(mfaRequired || mfaSetup) && (
              <div className="space-y-4">
                {mfaSetup && (
                  <div className="bg-cyan-50 p-6 rounded-2xl border border-cyan-100 text-center space-y-4">
                    <p className="text-[11px] font-bold text-cyan-900 uppercase tracking-widest leading-relaxed">
                      Initial MFA Setup Required
                    </p>
                    <div className="bg-white p-2 rounded-lg border border-cyan-100 flex justify-center">
                      <img 
                        src={mfaSetup.qrCodeUrl} 
                        alt="QR Code" 
                        className="w-48 h-48"
                      />
                    </div>
                    {mfaSetup.manualSetupCode && (
                      <div className="space-y-2">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Setup Secret:</p>
                        <code className="text-[11px] bg-white px-2 py-1 rounded border border-zinc-200 font-mono text-zinc-700 block select-all">
                          {mfaSetup.manualSetupCode}
                        </code>
                      </div>
                    )}
                    <p className="text-[9px] text-zinc-400 font-medium italic">
                      Scan this QR code with your authenticator app then enter the code below.
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest ml-1">
                    {usingBackupCode ? 'Recovery Code' : 'Authenticator Code'}
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input
                      type="text"
                      value={usingBackupCode ? backupCode : totp}
                      autoFocus
                      onChange={(e) => usingBackupCode ? setBackupCode(e.target.value) : setTotp(e.target.value)}
                      className={`w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3.5 pl-12 pr-6 text-sm text-center ${usingBackupCode ? 'tracking-wider' : 'tracking-[0.5em] text-lg'} font-mono text-zinc-900 focus:border-cyan-500/50 transition-all outline-none`}
                      placeholder={usingBackupCode ? "XXXXXXXX" : "000000"}
                      maxLength={usingBackupCode ? 8 : 6}
                      required
                    />
                  </div>
                </div>
                
                <div className="flex flex-col gap-2">
                  <button 
                    type="button"
                    onClick={() => setUsingBackupCode(!usingBackupCode)}
                    className="text-[9px] font-bold text-cyan-600 uppercase tracking-widest w-full text-center hover:text-cyan-700 transition-colors"
                  >
                    {usingBackupCode ? 'Use Authenticator App' : 'Lost Access? Use Recovery Code'}
                  </button>
                  
                  <button 
                    type="button"
                    onClick={() => { setMfaRequired(false); setMfaSetup(null); setTotp(''); setBackupCode(''); setUsingBackupCode(false); }}
                    className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest w-full text-center hover:text-zinc-600 transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            )}

            <TurnstileBox 
              show={showTurnstile} 
              onVerify={(token) => setTurnstileToken(token)} 
            />

            {error && (
              <div className="flex items-center gap-3 text-red-600 text-[11px] font-semibold uppercase bg-red-50 p-4 rounded-xl border border-red-100 animate-in tracking-wider">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>Error: {error}</span>
              </div>
            )}
 
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-zinc-950 text-white font-bold py-4 rounded-xl transition-all active:scale-[0.98] hover:bg-zinc-900 flex items-center justify-center gap-3 group disabled:opacity-50 uppercase tracking-widest text-xs shadow-lg shadow-zinc-950/10"
            >
              {isLoading ? 'Checking...' : 'Sign In'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
        
        <button 
          onClick={() => navigate('/')}
          className="mt-10 text-zinc-400 hover:text-zinc-600 transition-all text-[11px] font-bold uppercase tracking-widest w-full text-center"
        >
          Back to Homepage
        </button>
      </div>
    </div>
  );
};

export default AdminLogin;
